/**
 * THE CLASS LESSON RECORD (`class_sessions`), as a small state machine.
 *
 * Job #839 found two-thirds of real lesson rows with no end: the end was only
 * written when the teacher left the player inside the app. Closing the tab,
 * shutting the lid or switching the board off never wrote it. So the row is
 * now kept CLOSED AS THE LESSON GOES:
 *
 *   - `start` opens the row on the first clip that sounds, stamped with the
 *     LEGO that clip belongs to — never the launch screen's guess.
 *   - `checkpoint` re-writes the end figures at every round completion, so the
 *     last write is always a valid end even if nothing else ever lands.
 *   - `endOnUnload` sends a final write that survives the page going away
 *     (fetch keepalive), from pagehide / visibilitychange-hidden.
 *   - `end` is the in-app exit write.
 *
 * All three end writes land on the same row; the server keeps the furthest one (job #844), so they
 * are idempotent and order-independent: every one of them is a true end.
 */
export interface ClassLessonRoute {
  startClassSession?: (startLegoId: string) => Promise<{ id: string } | null>
  endClassSession?: (
    sessionId: string, endLegoId: string | null, cyclesCompleted: number, durationSeconds: number,
  ) => Promise<boolean>
  endClassSessionOnUnload?: (
    sessionId: string, endLegoId: string | null, cyclesCompleted: number, durationSeconds: number,
  ) => void
}

export function createClassLessonRecorder(
  getRoute: () => ClassLessonRoute | null | undefined,
  now: () => number = () => performance.now(),
) {
  let sessionId: string | null = null
  let starting = false
  let startedAt = 0
  let lastLegoId = ''

  const duration = () => Math.max(0, Math.floor((now() - startedAt) / 1000))

  return {
    get sessionId() { return sessionId },
    /** First clip that actually sounds. `playedLegoId` is that clip's own LEGO. */
    async start(playedLegoId: string | null | undefined, fallbackLegoId?: string | null): Promise<void> {
      if (sessionId || starting) return
      const startLegoId = playedLegoId || fallbackLegoId || 'S0001L01'
      starting = true
      startedAt = now()
      lastLegoId = startLegoId
      try {
        const row = await getRoute()?.startClassSession?.(startLegoId)
        if (row?.id) sessionId = row.id
      } catch (err) {
        // Never let a telemetry write touch playback.
        console.warn('[ClassLesson] Failed to start class session:', err)
      } finally {
        starting = false
      }
    },
    /** A round has completed: the class position moved, so the end moves with it. */
    async checkpoint(legoId: string, cyclesCompleted: number): Promise<void> {
      lastLegoId = legoId
      if (!sessionId) return
      try {
        await getRoute()?.endClassSession?.(sessionId, lastLegoId || null, cyclesCompleted, duration())
      } catch (err) {
        console.warn('[ClassLesson] Failed to checkpoint class session:', err)
      }
    },
    /** In-app exit. Closes the recorder; the row keeps its last end figures. */
    async end(cyclesCompleted: number): Promise<void> {
      const id = sessionId
      if (!id) return
      sessionId = null
      try {
        await getRoute()?.endClassSession?.(id, lastLegoId || null, cyclesCompleted, duration())
      } catch (err) {
        console.warn('[ClassLesson] Failed to end class session:', err)
      }
    },
    /** pagehide / hidden: synchronous, keepalive. The recorder stays open — a tab can come back. */
    endOnUnload(cyclesCompleted: number): void {
      if (!sessionId) return
      try {
        getRoute()?.endClassSessionOnUnload?.(sessionId, lastLegoId || null, cyclesCompleted, duration())
      } catch (err) {
        console.warn('[ClassLesson] Failed to send unload end:', err)
      }
    },
  }
}
